import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Tooltip,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon as MenuIconSlot,
  ListItemText as MenuTextSlot,
  Badge,
  Chip,
  Divider,
  CircularProgress,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import LogoutIcon from '@mui/icons-material/Logout';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BracketLogo from './BracketLogo';
import BreadcrumbsBar from '../shared/BreadcrumbsBar';
import { EntityDrawer, FieldRow } from '../ui';
import { ConfigContext } from '../shared/ConfigContext';
import { radius, layout } from '../../themes/smartinventoryTheme';
import { performLogout } from '../../utils/logout';
import { setTokens } from '../../utils/authTokens';
import useMemberships from '../../hooks/useMemberships';
import useCompanyQuota from '../../hooks/useCompanyQuota';
import { HelpProvider, useHelp } from '../../contexts/HelpContext';
import HelpPanel from '../help/HelpPanel';
import HelpTour, { hasSeenTour } from '../help/HelpTour';
import { useHelpBundle } from '../help/helpBundle';

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
};

const isActive = (pathname, to) => pathname === to || pathname.startsWith(`${to}/`);

// A route counts as a ":id" detail route — and gets breadcrumbs — when its
// last path segment is a Mongo ObjectId, matching how BreadcrumbsBar already
// resolves a friendly label for that segment.
const isDetailRoute = (pathname) => {
  const segments = pathname.split('/').filter(Boolean);
  const last = segments[segments.length - 1];
  return !!last && /^[a-f0-9]{24}$/i.test(last);
};

const NavItem = ({ item, active, onNavigate, dense }) => (
  <ListItemButton
    component={Link}
    to={item.to}
    onClick={onNavigate}
    selected={active}
    sx={{
      height: dense ? layout.mobileTapTarget : layout.navItemHeight,
      borderRadius: radius.pill,
      mb: 0.25,
    }}
  >
    <ListItemIcon sx={{ minWidth: 0, mr: 1.25 }}>
      <Badge color="primary" badgeContent={item.badge || 0} invisible={!item.badge}>
        <item.icon sx={{ fontSize: layout.navIconSize, color: active ? 'primary.main' : 'text.secondary' }} />
      </Badge>
    </ListItemIcon>
    <ListItemText primary={item.label} sx={item.locked ? { color: 'text.disabled' } : undefined} />
    {item.locked && <LockOutlinedIcon data-testid={`nav-lock-${item.to}`} sx={{ fontSize: 14, color: 'text.disabled' }} />}
  </ListItemButton>
);

const NavGroups = ({ nav, pathname, onNavigate, dense }) => (
  <List sx={{ px: 0.5 }}>
    {nav.map((group) => (
      <Box key={group.label} sx={{ mt: 1.5, '&:first-of-type': { mt: 0 } }}>
        <Typography
          variant="overline"
          component="div"
          sx={{ color: 'text.disabled', px: 1.5, mb: 0.5 }}
        >
          {group.label}
        </Typography>
        {group.items.map((item) => (
          <NavItem key={item.to} item={item} active={isActive(pathname, item.to)} onNavigate={onNavigate} dense={dense} />
        ))}
      </Box>
    ))}
  </List>
);

const RailNav = ({ nav, pathname }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, px: 0.5 }}>
    {nav.flatMap((group) => group.items).map((item) => {
      const active = isActive(pathname, item.to);
      return (
        <Tooltip key={item.to} title={item.label} placement="right">
          <IconButton
            component={Link}
            to={item.to}
            sx={{
              width: layout.railButtonSize,
              height: layout.railButtonSize,
              borderRadius: radius.sm,
              bgcolor: active ? 'primary.light' : 'transparent',
            }}
          >
            <Badge color="primary" badgeContent={item.badge || 0} invisible={!item.badge}>
              <item.icon sx={{ fontSize: layout.navIconSize, color: active ? 'primary.main' : 'text.secondary' }} />
            </Badge>
          </IconButton>
        </Tooltip>
      );
    })}
  </Box>
);

// Applies a fresh (companyId, role, tokens) after a successful create/switch
// and returns the path to navigate to — the caller does
// `window.location.href = applyCompanyContext(data)` (same shape as
// persistGoogleUser/persistMicrosoftUser). A hard reload, rather than a
// client-side state update, guarantees no cached/in-flight data from the
// previous company can bleed into the new one.
export const applyCompanyContext = (data) => {
  setTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
  localStorage.setItem('companyId', data.companyId || data._id);
  localStorage.setItem('userRole', data.role);
  return '/';
};

const CreateCompanyDrawer = ({ open, onClose, t }) => {
  const config = useContext(ConfigContext);
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleClose = () => {
    if (saving) return;
    setName('');
    setError('');
    onClose();
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      setError(t('Company name is required', { defaultValue: 'Company name is required' }));
      return;
    }
    setError('');
    setSaving(true);
    try {
      const res = await axios.post(`${config.backendUrl}/companies`, { companyName: name.trim() });
      window.location.href = applyCompanyContext(res.data);
    } catch (err) {
      console.error('Error creating company', err);
      setError(err.response?.data?.error || t('Error creating company.', { defaultValue: 'Error creating company.' }));
      setSaving(false);
    }
  };

  return (
    <EntityDrawer
      open={open}
      onClose={handleClose}
      title={t('Create a new company', { defaultValue: 'Create a new company' })}
      subtitle={t('You will become its admin.', { defaultValue: 'You will become its admin.' })}
      closeLabel={t('Close')}
      cancelLabel={t('Cancel')}
      primaryLabel={saving ? t('Creating...', { defaultValue: 'Creating...' }) : t('Create')}
      onPrimary={handleCreate}
      primaryDisabled={saving}
      loading={saving}
    >
      <FieldRow label={t('Company Name', { defaultValue: 'Company Name' })} error={error} required>
        <TextField
          fullWidth
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={saving}
          error={Boolean(error)}
        />
      </FieldRow>
    </EntityDrawer>
  );
};

const WorkspaceMenu = ({ anchorEl, onClose, toggleTheme, isDarkMode, t, workspace }) => {
  const navigate = useNavigate();
  const config = useContext(ConfigContext);
  const { memberships, loading, fetchMemberships } = useMemberships();
  const { canCreate, fetchQuota } = useCompanyQuota();
  const [switching, setSwitching] = useState(false);
  const [switchError, setSwitchError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const open = Boolean(anchorEl);
  // Global Admin's console workspace has no companyId — it isn't a member
  // of any single company, so switching doesn't apply there.
  const showSwitcher = Boolean(workspace?.companyId);

  useEffect(() => {
    if (open && showSwitcher) {
      setSwitchError('');
      fetchMemberships();
      fetchQuota();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // The current workspace is listed too (first, marked, not clickable) so
  // the menu answers "where am I?" as well as "where can I switch to?".
  const isCurrent = (m) => m.companyId === workspace?.companyId;
  const workspaces = [...(memberships || [])].sort((a, b) => Number(isCurrent(b)) - Number(isCurrent(a)));
  const chipLabel = (m) => {
    if (isCurrent(m)) return t('Current', { defaultValue: 'Current' });
    return m.role === 'admin' ? t('Admin') : t('User', { defaultValue: 'User' });
  };

  const handleSwitch = async (companyId) => {
    if (switching) return;
    setSwitching(true);
    setSwitchError('');
    try {
      const res = await axios.post(`${config.backendUrl}/auth/switch-company`, { companyId });
      window.location.href = applyCompanyContext(res.data);
    } catch (err) {
      console.error('Error switching company', err);
      setSwitchError(err.response?.data?.error || t('Could not switch companies.', { defaultValue: 'Could not switch companies.' }));
      setSwitching(false);
    }
  };

  return (
    <>
      <Menu anchorEl={anchorEl} open={open} onClose={onClose}>
        {showSwitcher && (
          <ListSubheader key="companies-header" sx={{ lineHeight: '32px' }}>
            {t('Switch workspace', { defaultValue: 'Switch workspace' })}
          </ListSubheader>
        )}
        {showSwitcher && loading && (
          <MenuItem key="loading" disabled>
            <CircularProgress size={14} sx={{ mr: 1.25 }} />
            {t('Loading...', { defaultValue: 'Loading...' })}
          </MenuItem>
        )}
        {showSwitcher && !loading && workspaces.map((m) => (
          <MenuItem
            key={m.companyId}
            onClick={isCurrent(m) ? undefined : () => handleSwitch(m.companyId)}
            disabled={switching || isCurrent(m)}
          >
            <MenuTextSlot sx={{ mr: 1 }}>{m.companyName || t('Company')}</MenuTextSlot>
            <Chip label={chipLabel(m)} size="small" color={isCurrent(m) ? 'primary' : 'default'} />
          </MenuItem>
        ))}
        {showSwitcher && switchError && (
          <MenuItem key="switch-error" disabled sx={{ whiteSpace: 'normal', color: 'error.main' }}>
            {switchError}
          </MenuItem>
        )}
        {showSwitcher && canCreate && (
          <MenuItem key="create-company" onClick={() => { onClose(); setCreateOpen(true); }}>
            <MenuIconSlot>
              <AddBusinessIcon fontSize="small" />
            </MenuIconSlot>
            <MenuTextSlot>{t('Create new company', { defaultValue: 'Create new company' })}</MenuTextSlot>
          </MenuItem>
        )}
        {showSwitcher && <Divider key="divider" />}
        <MenuItem onClick={() => { toggleTheme(); onClose(); }}>
          <MenuIconSlot>
            {isDarkMode ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
          </MenuIconSlot>
          <MenuTextSlot>{t('Toggle light/dark mode')}</MenuTextSlot>
        </MenuItem>
        <MenuItem onClick={() => { onClose(); performLogout(navigate); }}>
          <MenuIconSlot>
            <LogoutIcon fontSize="small" />
          </MenuIconSlot>
          <MenuTextSlot>{t('Logout')}</MenuTextSlot>
        </MenuItem>
      </Menu>
      {showSwitcher && (
        <CreateCompanyDrawer open={createOpen} onClose={() => setCreateOpen(false)} t={t} />
      )}
    </>
  );
};

const WorkspaceFooter = ({ workspace, compact, toggleTheme, isDarkMode, t }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const initials = getInitials(workspace.company || workspace.user);

  return (
    <>
      <Box
        onClick={(e) => setAnchorEl(e.currentTarget)}
        role="button"
        tabIndex={0}
        aria-label={t('Workspace menu')}
        sx={{
          mt: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          pt: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          cursor: 'pointer',
          justifyContent: compact ? 'center' : 'flex-start',
        }}
      >
        <Avatar>{initials}</Avatar>
        {!compact && (
          <>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="subtitle2" component="div" noWrap>{workspace.company}</Typography>
              <Typography variant="caption" component="div" color="text.secondary" noWrap>{workspace.user}</Typography>
            </Box>
            <UnfoldMoreIcon sx={{ fontSize: layout.navIconSize, color: 'text.secondary' }} />
          </>
        )}
      </Box>
      <WorkspaceMenu
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        toggleTheme={toggleTheme}
        isDarkMode={isDarkMode}
        t={t}
        workspace={workspace}
      />
    </>
  );
};

const MobileBottomBar = ({ items, onMore, pathname, t }) => (
  <Box
    sx={{
      display: 'flex',
      height: layout.bottomBarHeight,
      borderTop: '1px solid',
      borderColor: 'divider',
      bgcolor: 'background.paper',
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: (theme) => theme.zIndex.appBar,
    }}
  >
    {items.map((item) => {
      const active = isActive(pathname, item.to);
      return (
        <Box
          key={item.to}
          component={Link}
          to={item.to}
          sx={{
            flex: 1,
            minHeight: layout.mobileTapTarget,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.25,
            color: active ? 'primary.main' : 'text.secondary',
            textDecoration: 'none',
          }}
        >
          <Badge color="primary" badgeContent={item.badge || 0} invisible={!item.badge}>
            <item.icon sx={{ fontSize: layout.navIconSize }} />
          </Badge>
          <Typography variant="caption" sx={{ lineHeight: 1 }}>{item.label}</Typography>
        </Box>
      );
    })}
    <Box
      component="button"
      onClick={onMore}
      sx={{
        flex: 1,
        minHeight: layout.mobileTapTarget,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.25,
        color: 'text.secondary',
        bgcolor: 'transparent',
        border: 'none',
        font: 'inherit',
        cursor: 'pointer',
      }}
    >
      <MenuIcon sx={{ fontSize: layout.navIconSize }} />
      <Typography variant="caption" sx={{ lineHeight: 1 }}>{t('More')}</Typography>
    </Box>
  </Box>
);

const SidebarContent = ({ nav, workspace, toggleTheme, isDarkMode, t, pathname, onNavigate, dense }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', px: 1.5, py: 2.5, boxSizing: 'border-box' }}>
    <BracketLogo sx={{ px: 1, mb: 2 }} />
    <Box sx={{ flex: 1, overflowY: 'auto' }}>
      <NavGroups nav={nav} pathname={pathname} onNavigate={onNavigate} dense={dense} />
    </Box>
    <WorkspaceFooter workspace={workspace} toggleTheme={toggleTheme} isDarkMode={isDarkMode} t={t} />
  </Box>
);

// `enableHelp` mounts the in-view help panel (step 9) for this shell — set
// by the admin and GA routes in App.js, never by the customer shell. The
// HelpProvider wraps the whole shell (cheap, and PageHeader's help button
// needs it) regardless, but the panel UI itself only renders when enabled.
const AppShell = (props) => (
  <HelpProvider>
    <AppShellBody {...props} />
  </HelpProvider>
);

const AppShellBody = ({ nav, workspace, children, toggleTheme, isDarkMode, enableHelp = false }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const location = useLocation();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isMobile = !isDesktop && !isTablet;
  const [mobileOpen, setMobileOpen] = useState(false);
  const help = useHelp();
  const { isOpen: helpOpen, route: helpRoute, activeTour, startTour, closeTour } = help;
  const helpShifted = enableHelp && helpOpen && !isMobile;
  const helpBundle = useHelpBundle();

  // "Offered once on first visit, otherwise only on request" — a tour
  // auto-starts the first time this route is visited (desktop/tablet only;
  // tours are disabled below 900px), then never again on its own.
  useEffect(() => {
    if (!enableHelp || isMobile) return;
    if (!helpBundle.tours?.[helpRoute] || hasSeenTour(helpRoute)) return;
    startTour(helpRoute);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableHelp, isMobile, helpRoute, helpBundle]);

  const flatItems = nav.flatMap((group) => group.items);
  const bottomBarItems = flatItems.filter((item) => item.mobilePriority).slice(0, 4);
  const showBreadcrumbs = isDetailRoute(location.pathname);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {isDesktop && (
        <Box
          component="nav"
          sx={{
            width: layout.sidebarWidth, flexShrink: 0, borderRight: '1px solid', borderColor: 'divider',
            position: 'sticky', top: 0, height: '100vh',
          }}
        >
          <SidebarContent nav={nav} workspace={workspace} toggleTheme={toggleTheme} isDarkMode={isDarkMode} t={t} pathname={location.pathname} />
        </Box>
      )}

      {isTablet && (
        <Box
          component="nav"
          sx={{
            width: layout.railWidth,
            flexShrink: 0,
            borderRight: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 2,
            boxSizing: 'border-box',
            position: 'sticky',
            top: 0,
            height: '100vh',
          }}
        >
          <BracketLogo compact sx={{ mb: 2 }} />
          <Box sx={{ flex: 1, overflowY: 'auto', width: '100%' }}>
            <RailNav nav={nav} pathname={location.pathname} />
          </Box>
          <WorkspaceFooter workspace={workspace} compact toggleTheme={toggleTheme} isDarkMode={isDarkMode} t={t} />
        </Box>
      )}

      {isMobile && (
        <>
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              zIndex: (th) => th.zIndex.appBar,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              height: layout.mobileTopBarHeight,
              bgcolor: 'background.paper',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <IconButton onClick={() => setMobileOpen(true)} aria-label={t('Open menu')}>
              <MenuIcon />
            </IconButton>
            <BracketLogo />
          </Box>
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            PaperProps={{ sx: { width: layout.mobileDrawerWidth } }}
          >
            <SidebarContent
              nav={nav}
              workspace={workspace}
              toggleTheme={toggleTheme}
              isDarkMode={isDarkMode}
              t={t}
              pathname={location.pathname}
              onNavigate={() => setMobileOpen(false)}
              dense
            />
          </Drawer>
        </>
      )}

      <Box
        component="main"
        sx={(th) => ({
          flex: 1,
          minWidth: 0,
          px: `${layout.pagePaddingMobile}px`,
          pb: `${layout.pagePaddingMobile}px`,
          pt: isMobile ? `${layout.mobileTopBarHeight + layout.pagePaddingMobile}px` : `${layout.pagePaddingMobile}px`,
          mb: isMobile ? `${layout.bottomBarHeight}px` : 0,
          transition: 'margin-right 0.2s ease',
          [th.breakpoints.up('sm')]: {
            px: `${layout.pagePaddingTablet}px`,
            pb: `${layout.pagePaddingTablet}px`,
            pt: `${layout.pagePaddingTablet}px`,
          },
          [th.breakpoints.up('md')]: {
            px: `${layout.pagePaddingDesktop}px`,
            pb: `${layout.pagePaddingDesktop}px`,
            pt: `${layout.pagePaddingDesktop}px`,
          },
          maxWidth: layout.contentMax,
          ml: 'auto',
          mr: helpShifted ? `${layout.helpPanelWidth}px` : 'auto',
        })}
      >
        {showBreadcrumbs && <BreadcrumbsBar />}
        {children}
      </Box>

      {isMobile && (
        <MobileBottomBar items={bottomBarItems} onMore={() => setMobileOpen(true)} pathname={location.pathname} t={t} />
      )}

      {enableHelp && <HelpPanel />}
      {enableHelp && activeTour && <HelpTour route={activeTour} onClose={closeTour} />}
    </Box>
  );
};

export default AppShell;
